/*
 * CellX native cart helpers (replaces Rebuy Smart Cart + Rebuy widgets, 2026-09-22).
 *
 * - window.CXCart.add(items, trigger)  -> /cart/add.js with section rendering, re-renders + opens the native drawer
 * - [data-cx-add] buttons              -> one-click add (delegated, survives drawer re-renders)
 * - <cx-cart-discount>                 -> discount code apply/remove via /cart/update.js {discount}
 * - <cx-rotator>                       -> rotating announcement messages in the drawer
 * - <cx-recs>                          -> async fallback recommendations (intent=complementary, then related)
 * - <cx-stack-builder>                 -> "Arma tu stack" multi-add
 *
 * Reads via /cart.js only (never /cart.json: storefront-qa-fixes throttles GET /cart.json).
 * All cart mutations (POST /cart/add|change|update|clear, from this file AND theme cart.js qty/remove)
 * go through one FIFO queue so responses cannot render out of order.
 */
(function () {
  'use strict';
  if (window.CXCart) return;

  var MSG = {
    adding: 'Agregando…',
    added: 'Agregado',
    error: 'No se pudo agregar. Intenta de nuevo.',
    discountEmpty: 'Escribe un código de descuento.',
    discountInvalid: function (c) { return 'El código «' + c + '» no es válido o no aplica a tu carrito.'; },
    discountApplied: function (c) { return 'Código «' + c + '» aplicado.'; },
    discountDeferred: function (c) { return 'Revisaremos el código «' + c + '» al pagar.'; },
    discountError: 'No pudimos aplicar el código. Intenta de nuevo.',
    resynced: 'La conexión tardó más de lo normal. Actualizamos tu carrito: revisa los productos antes de continuar.'
  };

  /* ---------- Serialized cart mutations ---------- */
  var MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var queueTail = Promise.resolve();
  var QUEUE_TIMEOUT_MS = 15000;          // per-request abort
  var QUEUE_RELEASE_MS = QUEUE_TIMEOUT_MS + 5000; // queue moves on even if a request (or its resync) hangs
  function settleWithin(promise, ms) {
    return new Promise(function (resolve) {
      var t = setTimeout(resolve, ms);
      Promise.resolve(promise).then(function () { clearTimeout(t); resolve(); }, function () { clearTimeout(t); resolve(); });
    });
  }
  function readBody(res) {
    return res && typeof res.clone === 'function' ? res.clone().text().catch(function () {}) : null;
  }
  // Run fn() after every previously queued cart mutation has completed (response body read).
  // The release timer starts when fn() STARTS running (not when it is queued), so a slow earlier
  // request can never let two later requests overlap.
  function enqueue(fn) {
    var prev = queueTail;
    var run = prev.then(fn, fn);
    queueTail = prev.then(function () {
      return settleWithin(run.then(readBody), QUEUE_RELEASE_MS);
    }, function () {
      return settleWithin(run.then(readBody), QUEUE_RELEASE_MS);
    });
    return run;
  }

  // After a timed-out mutation the server may already have applied it: re-read the real cart and
  // re-render drawer + icon instead of letting the shopper retry (which could duplicate an add).
  function resyncCart() {
    var get = function (u) { return fetch(u, { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }); };
    return Promise.all([get('/cart.js'), get(((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + '?sections=cart-drawer,cart-icon-bubble') /* 2026-10-18 Herm: root, not the article URL (404 on blog posts) */])
      .then(function (res) {
        var state = res[0] || {};
        state.sections = res[1] || {};
        render(state, { open: false });
        notify('cx-resync');
        // Set after the caller's own catch() has run, so its generic error does not overwrite this.
        setTimeout(function () {
          var errs = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
          if (errs) errs.textContent = MSG.resynced;
        }, 50);
      })
      .catch(function () {});
  }

  if (window.fetch && !window.fetch.__cxQueued) {
    var prevFetch = window.fetch;
    var queuedFetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      if (method !== 'POST' || !MUTATION_RE.test(String(url).split('#')[0])) return prevFetch.apply(window, arguments);
      return enqueue(function () {
        // Every queued request (string URL or Request object, with or without a caller signal) gets a
        // 15s abort. A caller-supplied signal still works: aborting it aborts ours.
        var opts = Object.assign({}, init || {});
        var timer = null;
        var timedOut = false;
        if (typeof AbortController === 'function') {
          var ctrl = new AbortController();
          var callerSignal = opts.signal || (input && typeof input !== 'string' && input.signal) || null;
          if (callerSignal) {
            if (callerSignal.aborted) ctrl.abort();
            else callerSignal.addEventListener('abort', function () { ctrl.abort(); }, { once: true });
          }
          opts.signal = ctrl.signal;
          timer = setTimeout(function () { timedOut = true; ctrl.abort(); }, QUEUE_TIMEOUT_MS);
        }
        return prevFetch.call(window, input, opts).then(function (res) {
          clearTimeout(timer);
          return res;
        }, function (err) {
          clearTimeout(timer);
          if (timedOut) return resyncCart().then(function () { throw err; });
          throw err;
        });
      });
    };
    queuedFetch.__cxQueued = true;
    window.fetch = queuedFetch;
  }

  // Shopify money format (shop.money_format), e.g. "$ {{amount}}".
  function formatMoney(cents, format) {
    format = format || '${{amount}}';
    cents = Math.round(Number(cents) || 0);
    function fmt(n, dec, th, ds) {
      var parts = (n / 100).toFixed(dec).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, th);
      return parts[1] ? parts[0] + ds + parts[1] : parts[0];
    }
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, key) {
      switch (key) {
        case 'amount_no_decimals': return fmt(cents, 0, ',', '.');
        case 'amount_with_comma_separator': return fmt(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(cents, 2, "'", '.');
        default: return fmt(cents, 2, ',', '.');
      }
    });
  }

  function drawer() { return document.querySelector('cart-drawer'); }
  function isCartPage() { return !!document.querySelector('cart-items'); }

  function sectionIds() {
    var d = drawer();
    if (d && typeof d.getSectionsToRender === 'function') {
      return d.getSectionsToRender().map(function (s) { return s.id; });
    }
    return [];
  }

  function jsonPost(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (data) {
        return { ok: res.ok, status: res.status, data: data };
      });
    });
  }

  function notify(source) {
    try { document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: source } })); } catch (e) {}
  }

  // Re-render the drawer from a section-rendering response and (optionally) open it.
  function render(state, opts) {
    opts = opts || {};
    var d = drawer();
    if (isCartPage()) { window.location.reload(); return; }
    if (!d || !state || !state.sections) {
      if (opts.navigateOnFail) window.location.href = (window.routes && window.routes.cart_url) || '/cart';
      return;
    }
    var count = typeof state.item_count === 'number' ? state.item_count : null;
    if (typeof d.renderContents === 'function' && opts.open !== false) {
      d.renderContents(state);
    } else {
      // Refresh in place without re-triggering open().
      var cd = document.getElementById('CartDrawer');
      if (cd && state.sections['cart-drawer']) {
        var html = new DOMParser().parseFromString(state.sections['cart-drawer'], 'text/html').querySelector('#CartDrawer');
        if (html) cd.innerHTML = html.innerHTML;
      }
      var overlay = d.querySelector('#CartDrawer-Overlay');
      if (overlay && typeof d.close === 'function') overlay.addEventListener('click', d.close.bind(d));
      if (d.classList.contains('active')) {
        d.querySelectorAll('[data-aos]').forEach(function (el) { el.classList.add('aos-animate'); });
        if (typeof trapFocus === 'function' && cd) {
          trapFocus(cd, cd.querySelector('cx-cart-discount input[name="discount"]') || cd.querySelector('.drawer-inner'));
        }
      }
      var bubble = document.getElementById('cart-icon-bubble');
      if (bubble && state.sections['cart-icon-bubble']) {
        var b = new DOMParser().parseFromString(state.sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
        if (b) bubble.innerHTML = b.innerHTML;
      }
    }
    if (count !== null) d.classList.toggle('is-empty', count === 0);
    else d.classList.remove('is-empty');
  }

  function add(items, trigger) {
    var ids = sectionIds();
    var body = { items: items };
    if (ids.length) {
      body.sections = ids.join(',');
      body.sections_url = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/');
    }
    var d = drawer();
    // Focus returns here when the drawer closes. Buttons inside the drawer are destroyed by the re-render,
    // so use the header cart icon for those (and when there is no trigger).
    if (d && typeof d.setActiveElement === 'function') {
      var stable = trigger && !trigger.closest('cart-drawer') ? trigger : document.getElementById('cart-icon-bubble');
      if (stable) d.setActiveElement(stable);
    }
    return jsonPost('/cart/add.js', body).then(function (r) {
      if (!r.ok || r.data.status) {
        throw new Error((r.data && (r.data.description || r.data.message)) || MSG.error);
      }
      notify('cx-cart');
      render(r.data, { navigateOnFail: true });
      return r.data;
    });
  }

  function onAddClick(ev) {
    var btn = ev.target.closest && ev.target.closest('[data-cx-add]');
    if (!btn) return;
    ev.preventDefault();
    if (btn.getAttribute('aria-disabled') === 'true') return;
    var id = parseInt(btn.getAttribute('data-variant-id'), 10);
    if (!id) return;
    var qty = parseInt(btn.getAttribute('data-qty') || '1', 10) || 1;
    var label = btn.innerHTML;
    btn.setAttribute('aria-disabled', 'true');
    btn.classList.add('is-loading');
    btn.textContent = MSG.adding;
    add([{ id: id, quantity: qty }], btn).then(function () {
      if (document.body.contains(btn)) {
        btn.textContent = MSG.added;
        setTimeout(function () { btn.innerHTML = label; btn.removeAttribute('aria-disabled'); btn.classList.remove('is-loading'); }, 1800);
      }
    }).catch(function (e) {
      btn.innerHTML = label;
      btn.removeAttribute('aria-disabled');
      btn.classList.remove('is-loading');
      var err = btn.parentElement && btn.parentElement.querySelector('[data-cx-add-error]');
      if (err) { err.textContent = e.message || MSG.error; err.hidden = false; }
    });
  }
  document.addEventListener('click', onAddClick);

  /* ---------- Discount codes ---------- */
  if (!customElements.get('cx-cart-discount')) {
    customElements.define('cx-cart-discount', class extends HTMLElement {
      connectedCallback() {
        if (this._bound) return;
        this._bound = true;
        // Keep input events away from <cart-drawer-items> (its change handler updates line quantities).
        ['change', 'input'].forEach((t) => this.addEventListener(t, (e) => e.stopPropagation()));
        this.form = this.querySelector('form');
        this.input = this.querySelector('input[name="discount"]');
        this.msg = this.querySelector('[data-cx-discount-msg]');
        if (this.form) this.form.addEventListener('submit', (e) => { e.preventDefault(); this.apply(); });
        this.addEventListener('click', (e) => {
          var rm = e.target.closest('[data-cx-discount-remove]');
          if (rm) { e.preventDefault(); this.remove(rm.getAttribute('data-cx-discount-remove')); }
        });
      }
      applied() {
        return Array.prototype.map.call(this.querySelectorAll('[data-cx-discount-remove]'), function (b) {
          return b.getAttribute('data-cx-discount-remove');
        });
      }
      say(text, isError) {
        if (!this.msg) return;
        this.msg.textContent = text || '';
        this.msg.classList.toggle('is-error', !!isError);
        this.msg.hidden = !text;
      }
      busy(on) {
        this._busy = on;
        var b = this.querySelector('button[type="submit"]');
        if (b) { b.disabled = on; b.setAttribute('aria-busy', on ? 'true' : 'false'); }
        // No code removals while an apply/remove is in flight.
        this.querySelectorAll('[data-cx-discount-remove]').forEach(function (x) { x.disabled = on; });
      }
      send(codes) {
        var ids = sectionIds();
        var body = { discount: codes.join(',') };
        if (ids.length) { body.sections = ids.join(','); body.sections_url = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'); }
        return jsonPost('/cart/update.js', body);
      }
      apply() {
        var code = (this.input && this.input.value || '').trim();
        if (this._busy) return;
        if (!code) { this.say(MSG.discountEmpty, true); return; }
        var existing = this.applied().filter(function (c) { return c.toLowerCase() !== code.toLowerCase(); });
        this.busy(true);
        this.say('');
        this.send(existing.concat([code])).then((r) => {
          if (!r.ok) throw new Error('http');
          var codes = r.data && r.data.discount_codes;
          if (!Array.isArray(codes)) {
            // Older storefront API: fall back to the /discount/CODE cookie route.
            return enqueue(function () {
              return fetch('/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent('/cart.js'), { credentials: 'same-origin' });
            }).then(() => { this.say(MSG.discountDeferred(code), false); });
          }
          var hit = codes.filter(function (c) { return (c.code || '').toLowerCase() === code.toLowerCase(); })[0];
          if (!hit || hit.applicable === false) {
            // Drop the non-applicable code so it does not linger on the cart, then report.
            var keep = codes.filter(function (c) { return c.applicable !== false; }).map(function (c) { return c.code; });
            return this.send(keep).then((r2) => {
              var errText = MSG.discountInvalid(code);
              render(r2.data, { open: false });
              var fresh = document.querySelector('cx-cart-discount');
              if (fresh && fresh.say) fresh.say(errText, true); else this.say(errText, true);
            });
          }
          render(r.data, { open: false });
          var fresh = document.querySelector('cx-cart-discount');
          if (fresh && fresh.say) fresh.say(MSG.discountApplied(hit.code || code), false);
          notify('cx-discount');
        }).catch(() => {
          this.say(MSG.discountError, true);
        }).finally(() => { if (document.body.contains(this)) this.busy(false); });
      }
      remove(code) {
        if (this._busy) return;
        var keep = this.applied().filter(function (c) { return c !== code; });
        this.busy(true);
        this.send(keep).then((r) => {
          if (!r.ok) throw new Error('http');
          render(r.data, { open: false });
          notify('cx-discount');
        }).catch(() => { this.say(MSG.discountError, true); })
          .finally(() => { if (document.body.contains(this)) this.busy(false); });
      }
    });
  }

  /* ---------- Rotating announcements ---------- */
  // Pauses on hover/focus; with prefers-reduced-motion it never auto-rotates (CSS then shows every message).
  if (!customElements.get('cx-rotator')) {
    customElements.define('cx-rotator', class extends HTMLElement {
      connectedCallback() {
        var items = this.querySelectorAll('[data-cx-rotator-item]');
        clearInterval(this._t);
        if (items.length < 2) return;
        var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduce) { this.classList.add('is-static'); return; }
        var i = 0;
        var self = this;
        this._paused = false;
        if (!this._bound) {
          this._bound = true;
          var pause = function () { self._paused = true; };
          var resume = function () { if (!self.contains(document.activeElement)) self._paused = false; };
          this.addEventListener('mouseenter', pause);
          this.addEventListener('mouseleave', resume);
          this.addEventListener('focusin', pause);
          this.addEventListener('focusout', function () { setTimeout(resume, 0); });
        }
        this._t = setInterval(function () {
          if (!document.body.contains(self)) { clearInterval(self._t); return; }
          if (self._paused) return;
          items[i].hidden = true;
          i = (i + 1) % items.length;
          items[i].hidden = false;
        }, parseInt(this.getAttribute('data-interval') || '4000', 10));
      }
      disconnectedCallback() { clearInterval(this._t); }
    });
  }

  /* ---------- Async recommendation fallback ---------- */
  if (!customElements.get('cx-recs')) {
    customElements.define('cx-recs', class extends HTMLElement {
      connectedCallback() {
        if (this._loaded) return;
        this._loaded = true;
        // Server-rendered lists (pairings / cart) are final: never touch their wrapper.
        if (this.hasAttribute('data-rendered')) return;
        var urls = [this.getAttribute('data-url'), this.getAttribute('data-fallback-url')].filter(Boolean);
        if (!urls.length) return;
        var self = this;
        var load = function () {
          var next = function (idx) {
            if (idx >= urls.length) { self.closest('[data-cx-recs-wrap]') && (self.closest('[data-cx-recs-wrap]').hidden = true); return; }
            fetch(urls[idx], { credentials: 'same-origin' }).then(function (r) { return r.text(); }).then(function (html) {
              var doc = new DOMParser().parseFromString(html, 'text/html');
              var src = doc.querySelector('cx-recs[data-rendered]') || doc.querySelector('[data-cx-recs-list]');
              var list = src && (src.matches('[data-cx-recs-list]') ? src : src.querySelector('[data-cx-recs-list]'));
              if (list && list.querySelector('[data-cx-rec-item]')) {
                self.innerHTML = '';
                self.appendChild(list);
                var wrap = self.closest('[data-cx-recs-wrap]');
                if (wrap) wrap.hidden = false;
              } else {
                next(idx + 1);
              }
            }).catch(function () { next(idx + 1); });
          };
          next(0);
        };
        // The wrapper stays display:none until something loads, so no IntersectionObserver (a hidden or
        // zero-size target never intersects). Fetch once the page has loaded, when the browser is idle.
        // The section is hidden only after every URL came back empty (see next()).
        var start = function () {
          if (window.requestIdleCallback) window.requestIdleCallback(load, { timeout: 2000 });
          else setTimeout(load, 200);
        };
        if (document.readyState === 'complete') start();
        else window.addEventListener('load', start, { once: true });
      }
    });
  }

  /* ---------- Arma tu stack ---------- */
  if (!customElements.get('cx-stack-builder')) {
    customElements.define('cx-stack-builder', class extends HTMLElement {
      connectedCallback() {
        if (this._bound) return;
        this._bound = true;
        var mf = this.getAttribute('data-money-format');
        var cur = this.getAttribute('data-currency');
        var intl = cur && cur !== 'MXN' && window.Intl ? new Intl.NumberFormat(this.getAttribute('data-locale') || undefined, { style: 'currency', currency: cur }) : null;
        this.fmt = { format: function (pesos) { return intl ? intl.format(pesos) : formatMoney(Math.round(pesos * 100), mf); } };
        this.addEventListener('change', () => this.update());
        this.btn = this.querySelector('[data-cx-stack-add]');
        if (this.btn) this.btn.addEventListener('click', (e) => { e.preventDefault(); this.submit(); });
        this.update();
      }
      picked() { return Array.prototype.filter.call(this.querySelectorAll('input[data-variant-id]'), function (i) { return i.checked; }); }
      update() {
        var picked = this.picked();
        var total = picked.reduce(function (s, i) { return s + (parseInt(i.getAttribute('data-price'), 10) || 0); }, 0) / 100;
        var n = picked.length;
        var pct = n >= 6 ? 0.3 : (n >= 3 ? 0.2 : 0);
        var orig = this.querySelector('[data-cx-stack-original]');
        var fin = this.querySelector('[data-cx-stack-final]');
        var note = this.querySelector('[data-cx-stack-note]');
        if (orig) { orig.textContent = pct ? this.fmt.format(total) : ''; orig.hidden = !pct; }
        if (fin) fin.textContent = this.fmt.format(total * (1 - pct));
        if (note) {
          note.textContent = pct
            ? 'Ahorras ' + this.fmt.format(total * pct) + ' (' + Math.round(pct * 100) + '% aplicado automáticamente en el carrito)'
            : 'Agrega ' + (3 - n) + ' más para obtener 20% de descuento';
        }
        if (this.btn) this.btn.disabled = n === 0;
      }
      submit() {
        var items = this.picked().map(function (i) { return { id: parseInt(i.getAttribute('data-variant-id'), 10), quantity: 1 }; });
        if (!items.length) return;
        var btn = this.btn;
        var label = btn.textContent;
        btn.disabled = true;
        btn.textContent = MSG.adding;
        var err = this.querySelector('[data-cx-add-error]');
        if (err) err.hidden = true;
        add(items, btn).then(function () {
          btn.textContent = MSG.added;
          setTimeout(function () { btn.textContent = label; btn.disabled = false; }, 1800);
        }).catch(function (e) {
          btn.textContent = label;
          btn.disabled = false;
          if (err) { err.textContent = e.message || MSG.error; err.hidden = false; }
        });
      }
    });
  }

  window.CXCart = { add: add, render: render, enqueue: enqueue, formatMoney: formatMoney };
})();
